import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { Session, DEFAULT_SESSION } from '@/models/session';
import { fetchAllThreads } from '@/api/threads/fetchThreads';
import fetchPromptUsage from '@/api/users/fetchPromptUsage';
import { hasThreadsAccess } from '@/utils/session';
import { GTagEventData, gaPushEvent } from '@/utils/analytics';
import SessionContext from './SessionContext';
import { useQuery } from '@tanstack/react-query';
import { fetchSingleThread } from '@/api/threads/fetchThreads';
import {
  deleteFromProcessingQueue,
  isProcessingExpired,
} from '@/pages/Thread/utils/storage';
import { ThreadProcessingStatus } from '@/models/thread';

interface SessionProviderProps {
  children: ReactNode;
}

const PENDING_ACTIONS_INTERVAL = import.meta.env.VITE_TEST_MODE ? 3000 : 15000;

export const SessionProvider: FC<SessionProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session>(DEFAULT_SESSION);
  const pushUserEvent = useCallback(
    (action: string, data: GTagEventData = {}): void => {
      gaPushEvent(action, session.email, data);
    },
    [session.email]
  );

  const refreshThreads = () => {
    if (!hasThreadsAccess(session.roles)) return;

    setTimeout(() => {
      fetchPromptUsage(session.sub).then((promptUsage) => {
        if (promptUsage.remainingThisMonth === 0) {
          pushUserEvent('out_of_monthly_prompts');
        }

        fetchAllThreads(session.sub).then((threads) => {
          setSession((prev) => ({
            ...prev,
            threads,
            promptUsage,
          }));
        });
      });
    }, 4000);
  };

  const processingThreads = useMemo(() => {
    const asyncProcessingThreads = session.threads
      .filter(
        ({ id, processingStatus }) =>
          id && processingStatus === ThreadProcessingStatus.ASYNC
      )
      .map(({ id }) => id) as Array<string>;

    return asyncProcessingThreads;
  }, [session.threads]);

  const { refetch: refetchProcessingThreads } = useQuery<Array<string>>({
    queryKey: ['processing-threads-polling', processingThreads.join(',')],
    queryFn: async () => {
      const res: Array<string> = [...processingThreads];

      if (res.length > 0) {
        const threads = session.threads;
        let hasChanges = false;
        let stillProcessing = false;
        for (const id of processingThreads) {
          const storedThread = threads.find((thread) => thread.id === id);
          const storedThreadLastItem =
            storedThread?.items[storedThread.items.length - 1];
          try {
            const fetchedThread = await fetchSingleThread(session.sub, id);
            const threadIndex = threads.findIndex((thread) => thread.id === id);

            if (fetchedThread) {
              const lastItem =
                fetchedThread.items[fetchedThread.items.length - 1];
              if (
                threads[threadIndex].processingStatus &&
                ((lastItem.response.length > 0 &&
                  lastItem.id === storedThreadLastItem?.id) ||
                  isProcessingExpired(lastItem.queryDate))
              ) {
                // The single-thread endpoint has no `mode`, so keep what
                // we already knew about each item's approach.
                for (const item of fetchedThread.items) {
                  item.deepDive ??= storedThread?.items.find(
                    ({ id }) => id === item.id
                  )?.deepDive;
                }
                threads[threadIndex] = fetchedThread;
                threads[threadIndex].processingStatus = undefined;
                deleteFromProcessingQueue(id);
                pushUserEvent('async_thread_update_completed');
                hasChanges = true;
              } else {
                stillProcessing = true;
                if (!threads[threadIndex].processingStatus) {
                  threads[threadIndex].processingStatus =
                    ThreadProcessingStatus.ASYNC;
                  hasChanges = true;
                }
              }
            } else if (
              storedThreadLastItem?.queryDate &&
              !isProcessingExpired(storedThreadLastItem?.queryDate)
            ) {
              stillProcessing = true;
            } else {
              deleteFromProcessingQueue(id);
              hasChanges = true;
            }
          } catch (error) {
            stillProcessing = true;
            console.error('Error processing thread:', id, error);
          }
        }

        if (hasChanges) {
          setSession((prev) => ({
            ...prev,
            threads: [...threads],
          }));
        }

        if (stillProcessing) {
          setTimeout(refetchProcessingThreads, PENDING_ACTIONS_INTERVAL);
        }
      }

      return res;
    },
    initialData: [],
  });

  return (
    <SessionContext.Provider
      value={{
        session,
        setSession,
        refreshThreads,
        pushUserEvent,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
